import {
  createSelector,
  createSlice,
  current,
  PayloadAction,
} from "@reduxjs/toolkit";
import { IProjectIQWorkspace } from "models/IProjectIQWorkspace";

interface IProjectIQWorkspaceState {
  value: IProjectIQWorkspace[];
  isLoading: boolean;
}

const MILLISECONDS_PER_SECOND = 1000;

const initialState: IProjectIQWorkspaceState = {
  value: [],
  isLoading: true,
};

const projectIqWorkspacesSlice = createSlice({
  name: "projectIqWorkspacesSlice",
  initialState,
  reducers: {
    setProjectIqWorkspaces: (
      state,
      action: PayloadAction<IProjectIQWorkspace[]>,
    ) => {
      state.value = action.payload;
      state.isLoading = false;
    },
    // Replaces the list with fresh server data but keeps the existing object for
    // every workspace that did not change, so components that depend on a
    // workspace object (and reset local state when it changes) are not disturbed.
    refreshProjectIqWorkspaces: (
      state,
      action: PayloadAction<IProjectIQWorkspace[]>,
    ) => {
      const previousById = new Map(
        current(state.value).map((workspace) => [workspace.id, workspace]),
      );
      state.value = action.payload.map((incoming) => {
        const previous = previousById.get(incoming.id);
        return previous && JSON.stringify(previous) === JSON.stringify(incoming)
          ? previous
          : incoming;
      });
      state.isLoading = false;
    },
    addProjectIqWorkspace: (
      state,
      action: PayloadAction<IProjectIQWorkspace>,
    ) => {
      state.value.push(action.payload);
    },
    updateProjectIqWorkspace: (
      state,
      action: PayloadAction<IProjectIQWorkspace>,
    ) => {
      const index = state.value.findIndex(
        (workspace) => workspace.id === action.payload.id,
      );
      if (index !== -1) {
        state.value[index] = { ...state.value[index], ...action.payload };
      }
    },
    markProjectIqWorkspaceUpdated: (state, action: PayloadAction<string>) => {
      const workspace = state.value.find((w) => w.id === action.payload);
      if (workspace) {
        workspace.updatedAt = Math.floor(Date.now() / MILLISECONDS_PER_SECOND);
      }
    },
    removeProjectIqWorkspace: (state, action: PayloadAction<string>) => {
      state.value = state.value.filter(
        (workspace) => workspace.id !== action.payload,
      );
    },
    toggleWorkspacePin: (state, action: PayloadAction<string>) => {
      const workspace = state.value.find((w) => w.id === action.payload);
      if (workspace) workspace.isPinned = !workspace.isPinned;
    },
  },
});

export const {
  setProjectIqWorkspaces,
  refreshProjectIqWorkspaces,
  addProjectIqWorkspace,
  updateProjectIqWorkspace,
  markProjectIqWorkspaceUpdated,
  removeProjectIqWorkspace,
  toggleWorkspacePin,
} = projectIqWorkspacesSlice.actions;

const selectAllProjectIqWorkspaces = (state: {
  projectIqWorkspacesSlice: IProjectIQWorkspaceState;
}) => state.projectIqWorkspacesSlice.value;

export const selectAvailableProjectIqWorkspaces = createSelector(
  [selectAllProjectIqWorkspaces],
  (workspaces) => workspaces.filter((w) => !w.archived),
);

export const selectIsProjectIqWorkspacesLoading = (state: {
  projectIqWorkspacesSlice: IProjectIQWorkspaceState;
}): boolean => state.projectIqWorkspacesSlice.isLoading;

export default projectIqWorkspacesSlice.reducer;
